import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Grid, Card, Typography, Box, Stack } from "@mui/material";
import NewColors from "../../../../../../themes/NewColors";

/** Max distinct exercises in pie; remainder roll into "Other" so legend stays readable. */
const PIE_TOP_EXERCISES = 8;

// Component for displaying workout data in chart format
const WorkoutChart = ({ workoutSessions }) => {
  // Prepare data for charts
  const chartData = workoutSessions.map((workout) => {
    const workoutDate = new Date(workout.date);
    const isValidDate = !isNaN(workoutDate.getTime());
    
    return {
      date: isValidDate 
        ? workoutDate.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })
        : "Invalid Date",
      exercises: workout.exercises.length,
      completedSets: workout.completedSets,
      totalSets: workout.totalSets,
      completionRate:
        Number(((workout.completedSets / workout.totalSets) * 100).toFixed(1)) ||
        0,
      completed: workout.completed,
    };
  });

  // Exercise type distribution
  const exerciseTypes = {};
  workoutSessions.forEach((workout) => {
    workout.exercises.forEach((exercise) => {
      exerciseTypes[exercise.exerciseName] =
        (exerciseTypes[exercise.exerciseName] || 0) + 1;
    });
  });

  // Generate consistent colors for pie chart - matching the screenshot colors
  const COLORS = [
    "#9575CD",
    "#FF8A65",
    "#4DB6AC",
    "#FFB74D",
    "#F06292",
    "#7986CB",
  ];

  const sortedExercises = Object.entries(exerciseTypes).sort(
    (a, b) => b[1] - a[1],
  );
  const topSlice = sortedExercises.slice(0, PIE_TOP_EXERCISES);
  const otherCount = sortedExercises
    .slice(PIE_TOP_EXERCISES)
    .reduce((sum, [, c]) => sum + c, 0);

  const pieData = topSlice.map(([name, count]) => ({ name, value: count }));
  if (otherCount > 0) {
    const rest = sortedExercises.length - topSlice.length;
    pieData.push({
      name: `Other (${rest} type${rest === 1 ? "" : "s"})`,
      value: otherCount,
    });
  }

  return (
    <Grid container spacing={3} sx={{ width: "100%" }}>
      {/* Workout Progress Over Time — full width until lg so pie column is not squeezed */}
      <Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
        <Card sx={{ p: 2, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Workout Progress Over Time
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length && payload[0]) {
                    const data = payload[0].payload;
                    return (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          boxShadow: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {label}
                        </Typography>
                        <Typography color="primary" variant="body2">
                          Completion Rate: {payload[0].value}%
                        </Typography>
                        <Typography color="info.main" variant="body2">
                          Sets: {data.completedSets}/{data.totalSets}
                        </Typography>
                        <Typography color="success.main" variant="body2">
                          Exercises: {data.exercises}
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Line
                type="monotone"
                dataKey="completionRate"
                stroke={NewColors.charts.blue}
                strokeWidth={3}
                dot={{ fill: NewColors.charts.blue, strokeWidth: 2, r: 6 }}
                activeDot={{
                  r: 8,
                  stroke: NewColors.charts.blue,
                  strokeWidth: 2,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Grid>

      {/* Exercise distribution: top exercises in period + Other; scrollable legend */}
      <Grid item xs={12} lg={4} sx={{ minWidth: 0 }}>
        <Card sx={{ p: 2, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Exercise Distribution
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Top {PIE_TOP_EXERCISES} exercises by set appearances in this list
            {otherCount > 0 ? "; remaining types grouped as Other" : ""}
          </Typography>
          {pieData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No exercise breakdown for these workouts
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
              <Box sx={{ width: "100%", height: 220, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={72}
                      fill={NewColors.charts.blue}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value}`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{
                  maxHeight: 128,
                  overflowY: "auto",
                  overflowX: "hidden",
                  width: "100%",
                  minWidth: 0,
                  pr: 0.5,
                }}
              >
                {pieData.map((d, index) => (
                  <Stack
                    direction="row"
                    key={d.name}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ py: 0.25 }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        mt: 0.5,
                        flexShrink: 0,
                        borderRadius: 0.5,
                        bgcolor: COLORS[index % COLORS.length],
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ wordBreak: "break-word", lineHeight: 1.35 }}
                    >
                      <strong>{d.value}</strong> — {d.name}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>
          )}
        </Card>
      </Grid>

      {/* Sets Completion Chart */}
      <Grid item xs={12} sx={{ minWidth: 0 }}>
        <Card sx={{ p: 2, minWidth: 0, overflow: "hidden" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Sets Completion by Workout
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          boxShadow: 2,
                        }}
                      >
                        <Typography variant="subtitle2">{label}</Typography>
                        <Typography color="success.main" variant="body2">
                          Completed: {payload[0].value} sets
                        </Typography>
                        <Typography color="warning.main" variant="body2">
                          Remaining: {payload[1].value - payload[0].value} sets
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="completedSets"
                fill="#4caf50"
                name="Completed Sets"
              />
              <Bar dataKey="totalSets" fill="#ff9800" name="Total Sets" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Grid>
    </Grid>
  );
};

export default WorkoutChart;
